import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useUrlBool, useUrlString, useUrlRange } from '../../hooks/useUrlState';
import { useConfirmedMutation } from '../../lib/confirm';
import { attendanceFlag } from '../../lib/attendanceFlags';
import { Card, Tag, Typography, DatePicker, Select, Space, Row, Col, Button, Tooltip, EmptyState, message, Modal, Form, Input, Tabs, Badge, Segmented, Table, Tag as TwTag, Tooltip as TwTooltip, StatCard, cn } from '../../ui';
import type { StatTone } from '../../ui';
import { Eye, MapPin, AlertTriangle, CheckCircle2, Clock, Users, Plus, RefreshCw, UserX, CalendarDays, CalendarClock, Route as RouteIcon, Download, Search, Store, PackageCheck, Hourglass } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission } from '../../store/auth';
import { useUrlTab } from '../../hooks/useUrlTab';
import { TwDataTable as DataTable, type DataTableColumn } from '../../components/TwDataTable';
import { useFilterPanel, hueOption, type FilterChip, type FilterGroup } from '../../components/FilterPanel';
import { RegularizationReviewCard } from './regularization';
import { HolidaysCard } from './HolidaysCard';
import { AttendanceUserDrawer, type AttUser } from './AttendanceUserDrawer';
import { AttendanceDetailModal } from './AttendanceDetailModal';
import { STATUS_OPTIONS, STATUS_COLOR, SOURCE_TAG, fmtIdle, fmtWorked, mapsUrl, isHeadRole } from './attendanceMeta';
import { ExportDialog } from '../../components/ExportDialog';
import { RoleBadge, staffPickerOptions } from '../../components/RoleBadge';
import { GrantCoverageModal } from '../territory/GrantCoverageModal';
import { TerritoryNodeChips } from '../../components/TerritoryChips';
import { RouteMapModal } from '../../components/RouteMapModal';
import { useBadges } from '../../hooks/useBadges';
import { formatIstDate, formatIstTime } from '../../lib/time';
import { useReverseGeocode } from '../../lib/reverseGeocode';
import { useStaffTerritories, territoryIdOf, NO_TERRITORY, type TerritoryPick, type TerritoryNode } from '../../lib/useStaffTerritories';
import { inr } from '../../lib/format';
import { PALETTE, TAG_TONE_HUE } from '../../lib/palette';
import { useServerSort } from '../../hooks/useServerSort';

/** The "everything at this level" choice in the territory cascade. A real option rather than an
 *  empty select, so the control reads as ANSWERED ("All states") instead of merely unset. */
const ALL_T = '*';

/**
 * "Last location" table cell — reverse-geocodes the rep's latest activity coords into a full,
 * human-readable address (cached per-coordinate, so each place is looked up only once). Shows the
 * activity type + time as a subline and links straight to Google Maps. Falls back to a plain map
 * link (never bare lat/lng) if the geocode is unavailable.
 */
function LastLocationCell({ lat, lng, type, at }: { lat?: string | null; lng?: string | null; type?: string | null; at?: string | null }) {
  const geo = useReverseGeocode(lat, lng);
  const url = mapsUrl(lat, lng);
  if (!url) return <span className="text-muted">—</span>;
  const meta = `${type ?? 'Location'}${at ? ` · ${formatIstTime(at)}` : ''}`;
  return (
    <a
      href={url} target="_blank" rel="noreferrer" title={meta}
      // The row opens the detail drawer; this link opens Google Maps. Without this the click would
      // do both.
      onClick={(e) => e.stopPropagation()}
      className="group inline-flex max-w-[280px] items-start gap-1.5 text-brand-700! hover:text-brand-800! dark:text-brand-400!"
    >
      <MapPin size={14} className="mt-0.5 shrink-0" />
      <span className="min-w-0">
        <span className="line-clamp-2 type-body-medium leading-snug group-hover:underline">
          {geo.isFetching ? <span className="text-muted">Resolving address…</span> : (geo.data ?? 'View on map')}
        </span>
        {at && <span className="mt-0.5 block truncate text-2xs text-subtle">{meta}</span>}
      </span>
    </a>
  );
}

/**
 * Staff cell — the name with a COLOURED ROLE BADGE beside it, over a caption naming where the person
 * works.
 *
 * The role used to be a caption under the name, toned amber for a head and grey for a rep. In a mixed
 * list that reads as two lines of text you have to actually read; as a pill next to the name it is a
 * colour you can scan a column for. The freed caption line now carries the rep's territory book,
 * which is what the new territory filters narrow on — so the filter's effect is visible in the rows.
 */
function StaffCell({ name, role, userId, book }: { name?: string | null; role?: string | null; userId?: string | number; book?: { id?: string; level: string; name: string }[] }) {
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="truncate font-medium text-content">{name ?? `#${userId}`}</span>
        <RoleBadge role={role} />
      </div>
      {/* The book as chips, each in its level's colour — the same blue/cyan/green a dealer's address
          wears, so "beat" means one thing everywhere. It was "Katol · Maharashtra" in grey, which
          asks the reader to already know that one is a beat and the other a whole state. */}
      <div className="mt-0.5"><TerritoryNodeChips nodes={book ?? []} max={2} hideWhenEmpty /></div>
    </div>
  );
}

/** Delegates to the shared StatCard. Besides the look, this fixes a real a11y problem: the old tile
 *  was a clickable <div> with no role, tabIndex or key handler, so these filters were mouse-only.
 *  StatCard renders a real <button> when `onClick` is given.
 *
 *  Sizing belongs to the BAND, not the tile: these used to be `flex: 1 1 150px` in a wrap row, which
 *  was fine at five tiles and absurd at eight — the last one wrapped alone and grew to the full page
 *  width, a banner where a tile was meant. The band is a grid now, so a tile is one cell whatever the
 *  count, and a wrapped row lines up with the one above it.
 *
 *  Takes a `tone` rather than an accent hex: these tiles used to route hexes through `statTone()`,
 *  which resolved most of them to the same grey, so five differently-meaning KPIs rendered as one
 *  colour. Naming the tone states the intent directly and can't drift. */
/**
 * Does this day still need a human to look at it? The client-side twin of the SQL behind the tab
 * badge (BadgesService.ATT_ANOMALY / ATT_INCOMPLETE), so the number and the rows it opens agree:
 *
 *  • the punch is doubted — mock GPS, face mismatch, outside the geofence, or the 22:00 sweep
 *    closed a day the rep never punched out of (the hours were guessed, not measured); or
 *  • a PAST day carries only one punch. Today's open shift is not a defect, it is a shift.
 *
 * Synthetic ABSENT rows are days with no record at all, so there is nothing on them to review.
 */
function needsReview(r: any): boolean {
  if (r.synthetic) return false;
  if ((r.anomalyFlags?.length ?? 0) > 0) return true;
  const halfPunched = !!r.checkInAt !== !!r.checkOutAt;
  return halfPunched && String(r.logDate ?? '') < dayjs().format('YYYY-MM-DD');
}

/**
 * One fact in the export dialog's "what this file will contain" block — label left, value right.
 *
 * The dialog carries no period picker and no staff tick-list any more (this register's own filters
 * are the file's), so this block is the only place a reader can check the scope before downloading.
 */
function ScopeRow({ label, tone, children }: { label: string; tone?: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-16 shrink-0 pt-px type-label text-subtle">{label}</span>
      <span className={cn('min-w-0 flex-1 type-body-medium text-content', tone)}>{children}</span>
    </div>
  );
}

function Kpi({ title, value, tone, icon, onClick, active, hint, dead }: {
  title: string; value: React.ReactNode; tone: StatTone; icon: React.ReactNode;
  onClick?: () => void; active?: boolean;
  /** The second line — what the number is made of, or what clicking will show. */
  hint?: React.ReactNode;
  /** A tile that counts nobody must not be clickable: it could only ever empty the table. */
  dead?: boolean;
}) {
  return (
    <StatCard
      label={title}
      value={value}
      hint={hint}
      tone={tone}
      icon={icon}
      onClick={dead ? undefined : onClick}
      active={active}
      className="min-w-0"
    />
  );
}

export function AttendancePage() {
  const [range, setRange] = useUrlRange('range', [dayjs().subtract(7, 'day'), dayjs()]);
  const [anomalyOnly, setAnomalyOnly] = useUrlBool('anomaly');
  /*
   * "Needs review" — the slice the TAB BADGE counts: a day whose punch is doubted (anomaly flags)
   * OR a past day left half-punched. Wider than `anomaly` on purpose, because the badge adds both
   * kinds together and a number must open everything it counted.
   */
  const [reviewOnly, setReviewOnly] = useUrlBool('review');
  const [activeOnly, setActiveOnly] = useUrlBool('active', true);
  const [openOnly, setOpenOnly] = useUrlBool('open');  // Open-shift KPI drill-down (checked in, not out)
  // Output drill-downs: days that produced a visit / an order. Same mechanic as the other KPI tiles.
  const [visitsOnly, setVisitsOnly] = useUrlBool('visits');
  const [ordersOnly, setOrdersOnly] = useUrlBool('orders');
  const [pendingOnly, setPendingOnly] = useUrlBool('pending');  // reps carrying a visit they never closed
  // Flat list vs per-day summary — in the URL so Back returns to the view you were in.
  const [view, setView] = useUrlTab('view', ['list', 'byday'] as const, 'list');
  const [section, setSection] = useUrlTab('tab', ['log', 'reg', 'holidays'] as const, 'log');
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  const [userId, setUserId] = useUrlString<string | undefined>('user', undefined);
  // Free-text staff lookup + the territory book the rep works, both narrowing the already-fetched
  // window client-side (see `scopedRows`) so the KPI denominators never move under a filter.
  const [search, setSearch] = useUrlString('q');
  // Territory ids, not names: two states can hold a district of the same name.
  /*
   * The territory cascade is one filter made of three linked ids, so it travels as one param
   * (`state~district~beat`) rather than three that could be restored half-applied.
   */
  const [pickRaw, setPickRaw] = useUrlString('terr');
  const pick = useMemo<TerritoryPick>(() => {
    const [state, district, beat] = pickRaw.split('~');
    return { state: state || null, district: district || null, beat: beat || null };
  }, [pickRaw]);
  const setPick = useCallback(
    (p: TerritoryPick) => setPickRaw([p.state ?? '', p.district ?? '', p.beat ?? ''].join('~').replace(/~+$/, '')),
    [setPickRaw],
  );
  const [detailId, setDetailId] = useState<string | null>(null);
  const [userDrawer, setUserDrawer] = useState<AttUser | null>(null);
  // Opens the field-movement route map for one staff member on one day (moved here from Sales Performance).
  const [routeRow, setRouteRow] = useState<{ id: string; name: string; date: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // After a leave range that touches today/future is marked for an SP who owns territories,
  // we chain into Assign-coverage pre-filled (skippable). Only for markers who can grant coverage.
  const [coveragePrompt, setCoveragePrompt] = useState<{ absentId: string; from: string; to: string } | null>(null);
  const canManage = usePermission('ATTENDANCE.MANAGE');
  const canViewUsers = usePermission('USER.VIEW');
  // Matches the server's ATTENDANCE.EXPORT gate. The timesheet is payroll input, so it is granted
  // separately from ATTENDANCE.VIEW.
  const canExport = usePermission('ATTENDANCE.EXPORT');
  // Marking attendance ON BEHALF of a rep is SA-only by default via FIELD.ONBEHALF (editable in
  // Roles & Permissions) — separate from the broad ATTENDANCE.MANAGE used for status overrides / leave.
  const canOnBehalf = usePermission('FIELD.ONBEHALF');
  const canCoverage = usePermission('TERRITORY.COVERAGE');
  const canRoute = usePermission('DASH.SALES'); // gate the movement-route column (same perm as the route endpoint)
  const canApproveReg = usePermission('ATTENDANCE.REGULARIZE.APPROVE');
  const badges = useBadges(); // anomalies + pending regularizations, for the tab indicators

  // Fetch the whole window once (real rows of every status + synthetic absents). Status / anomaly /
  // open-shift / active filtering is done client-side below, so the KPI totals stay stable and every
  // card can drill the table without a refetch.
  // The sort is the SERVER's. These rows are a stored-punch query merged with synthetic ABSENT days,
  // and the output columns (visits / customers / orders / value) are attached server-side after that
  // merge — so only the backend can order the whole answer. The client-side status/anomaly filtering
  // below only removes rows, which leaves that order intact.
  const { sortState, onSortChange, sort } = useServerSort();

  const params = useMemo(() => {
    const p: Record<string, string> = {
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
      absent: '1',
    };
    if (userId) p.userId = userId;
    if (sort) p.sort = sort;
    return p;
  }, [range, userId, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ['attendance', params],
    queryFn: () => request<any[]>({ url: '/attendance', params }),
    placeholderData: keepPreviousData,
  });

  const { data: users } = useQuery({
    // Field staff = Sales Persons AND Sales Heads (a Head also works his own beat, so his attendance is
    // tracked too). Drives the staff filter + the on-behalf "Mark attendance" picker.
    queryKey: ['field-staff-users'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON,SALES_HEAD' }),
    // /users needs USER.VIEW. A Sales Person can open this page to see their OWN rows but cannot list
    // staff, so firing this unconditionally logged a 403 for them on every visit.
    enabled: canViewUsers,
  });
  // Holidays label weekly-off / holiday days in the per-day view.
  const { data: holidays } = useQuery({
    queryKey: ['holidays'],
    queryFn: () => request<any[]>({ url: '/holidays' }),
  });

  // Ids of the field staff this page is about — the population the territory counts describe, so a
  // dropdown reading "Maharashtra (9)" means nine SP/SH, not nine users of any kind.
  const fieldStaffIds = useMemo(() => ((users?.data ?? []) as any[]).map((u) => String(u.id)), [users]);
  // Where each rep works (beat → district → state): drives the territory filters, the staff caption
  // and the export picker. Hidden entirely for an actor who can't read the territory masters.
  const terr = useStaffTerritories(fieldStaffIds);

  // Staff pickers list Sales Heads AND Sales Persons, so they're GROUPED by role (heads first) rather
  // than run together — a flat "Vikram - SH / Asmita - SP" list gives the eye nothing to anchor on.
  // Multi-role users are filed under their most senior role.
  //
  // Each name also WEARS its role, as the same coloured pill the table's staff column and the export
  // dialog use. The group headings alone stopped short in two places: they scroll off the top of a
  // long list, so most of what you are looking at sits under no visible heading; and a rep who is
  // ALSO a Head is filed under ONE heading while holding TWO roles, which a heading cannot say. The
  // pill travels with the name, so a row states what the person is wherever it is read.
  //
  // `name` is the plain text — what type-to-search matches, what the applied-filter chip prints, and
  // what the CLOSED control shows: a pill inside a 9rem filter cell would push the name it belongs
  // to out of view.
  // Built by `staffPickerOptions` (components/RoleBadge) — the coverage register's staff filter is
  // the same list of the same people, and two copies is how one of them loses its badges.
  const staffOptions = useMemo(() => staffPickerOptions((users?.data ?? []) as any[]), [users]);

  /**
   * WHO is in view — the name search and the territory book, applied once for BOTH views.
   *
   * Kept separate from the status/anomaly narrowing below because these two answer a different
   * question ("which people?" vs "which of their days?"), and because the per-day rollup needs the
   * same set of people while keeping every status. Splitting them is what stops the List and By-day
   * tabs from silently disagreeing about who works in Pune.
   */
  const scopedRows = useMemo(() => {
    let recs = (data ?? []) as any[];
    const q = search.trim().toLowerCase();
    if (q) recs = recs.filter((r) => `${r.staffName ?? ''} ${r.staffRole ?? ''}`.toLowerCase().includes(q));
    // The DEEPEST node picked is the filter, and a rep matches when it is anywhere in their book —
    // so choosing a state keeps every rep on its districts and beats, not only someone bound at
    // state level.
    const node = territoryIdOf(pick);
    if (node) recs = recs.filter((r) => terr.matches(r.userId, node));
    return recs;
  }, [data, search, pick, terr]);

  /**
   * The KPI band, counted over WHO IS IN VIEW but ignoring the tile filters themselves.
   *
   * Two different denominators are deliberately in play. The band respects the staff/territory scope
   * — "6 present" has to mean six of the people on screen, or picking a district leaves the tiles
   * describing somebody else — but it ignores status / anomaly / open-shift / output narrowing, so a
   * tile never counts only the rows its own filter produced and the numbers hold still while you
   * drill through them.
   */
  const kpis = useMemo(() => {
    // Real (stored) rows vs synthetic ABSENT rows the backend folds in. Present/Open/Anomaly count
    // only real records; Absent counts both admin-marked ABSENT and synthesized absences.
    const real = scopedRows.filter((r: any) => !r.synthetic);
    const present = real.filter((r: any) => r.status === 'PRESENT').length;
    /*
     * The review queue, in-period half.
     *
     * This used to count anomaly FLAGS only, while the tab badge and the backlog note beside it
     * count flags OR a past day left half-punched. The two read as one quantity split in two —
     * "1 here, 7 older, 8 in all" — so a day that was merely half-punched made that sum visibly
     * wrong: it was absent from the tile and present in the total. `needsReview` is the same
     * predicate the badge's SQL uses, so the halves always add up.
     */
    const anomalies = real.filter(needsReview).length;
    const openShift = real.filter((r: any) => r.checkInAt && !r.checkOutAt).length;
    const absent = scopedRows.filter((r: any) => r.status === 'ABSENT').length;
    // Output is summed over EVERY row, synthetic included: a rep can be off the roster and still
    // have booked an order from the office, and dropping it would understate the period.
    const sum = (k: string) => scopedRows.reduce((n: number, r: any) => n + Number(r[k] ?? 0), 0);
    const visits = sum('visitCount');
    const orders = sum('orderCount');
    const orderValue = sum('orderValue');
    const rejected = sum('ordersRejected');
    // Coverage is a PER-REP period figure repeated on each of that rep's rows, so it is counted once
    // per person. Summing the column instead would multiply every rep's book by the number of days
    // they happen to have in the window — a tile that grows when you widen the dates.
    const perRep = new Map<string, { pending: number; book: number; covered: number }>();
    for (const r of scopedRows as any[]) {
      const k = String(r.userId);
      if (!perRep.has(k)) {
        perRep.set(k, {
          // The PERIOD fields, not the day ones. `customersVisited` is now a day count, and summing
          // one rep's first day as if it were their whole period is how "4 of 2,262 covered" came to
          // be printed under a figure derived from the period.
          pending: Number(r.periodCustomersNotVisited ?? 0),
          book: Number(r.customersOnBook ?? 0),
          covered: Number(r.periodCustomersCovered ?? 0),
        });
      }
    }
    const perRepList = [...perRep.values()];
    const pending = perRepList.reduce((a, b) => a + b.pending, 0);
    const pendingReps = perRepList.filter((x) => x.pending > 0).length;
    const book = perRepList.reduce((a, b) => a + b.book, 0);
    const covered = perRepList.reduce((a, b) => a + b.covered, 0);
    return {
      records: real.length, present, absent, anomalies, openShift,
      // What one row IS: a STAFF-DAY -- one person on one day. Every tile here is a row count sitting
      // under a date range, so the unit has to be said out loud (see the line above the band) or a
      // bare "33" over an eight-week window is read as "33 of those 56 days".
      staffDays: scopedRows.length,
      visits, orders, orderValue, rejected, pending, pendingReps, book, covered,
      // How many DISTINCT reps the period figures were folded from — the denominator of "6 books".
      reps: perRepList.length,
      // How many DAYS produced something — what the tile's own drill-down will actually show.
      visitDays: scopedRows.filter((r: any) => Number(r.visitCount ?? 0) > 0).length,
      orderDays: scopedRows.filter((r: any) => Number(r.orderCount ?? 0) > 0).length,
    };
  }, [scopedRows]);

  // Calendar days the pickers span -- the figure the reader silently compares every tile against.
  const windowDays = Math.max(1, range[1].diff(range[0], 'day') + 1);

  // Client-side filtering (the query already fetched the whole window). "Active only" shows worked days
  // (Present / Half-day); a status / open-shift / anomaly focus narrows further. Synthetic ABSENT rows
  // only surface in the Absent view.
  /*
   * The drill-down filters (a tile / status / Needs review), as ONE predicate shared by both views.
   * By day used to ignore them while still printing their chips, so "Needs review" showed one day in
   * the List and every day in By day — which read as attendance missing from the List.
   */
  const drillOn = !!status || anomalyOnly || reviewOnly || openOnly || visitsOnly || ordersOnly;
  const drillMatch = useCallback((r: any) => {
    if (status && r.status !== status) return false;
    if (anomalyOnly && !((r.anomalyFlags?.length ?? 0) > 0)) return false;
    if (reviewOnly && !needsReview(r)) return false;
    if (openOnly && !(r.checkInAt && !r.checkOutAt)) return false;
    if (visitsOnly && !(Number(r.visitCount ?? 0) > 0)) return false;
    if (ordersOnly && !(Number(r.orderCount ?? 0) > 0)) return false;
    return true;
  }, [status, anomalyOnly, reviewOnly, openOnly, visitsOnly, ordersOnly]);

  const tableRows = useMemo(() => {
    let recs = scopedRows;
    if (activeOnly) recs = recs.filter((r) => r.status === 'PRESENT' || r.status === 'HALF_DAY');
    if (drillOn) recs = recs.filter(drillMatch);
    /*
     * A synthetic ABSENT row is a day nobody punched, so it is noise in a punch list — EXCEPT when
     * the tile that opened this view COUNTED it.
     *
     * Visits and Orders sum over every row, synthetic included, and deliberately so: a rep off the
     * roster can still book an order from the office. Dropping those rows here made the drill-down
     * contradict the tile that opened it — "Orders 23 on 8 staff-days" opened a single row holding
     * 2, because seven of those eight staff-days were days the rep never punched in. The orders are
     * real; the attendance is what is missing, and that is worth SEEING rather than hiding.
     */
    const keepSynthetic = status === 'ABSENT' || visitsOnly || ordersOnly;
    if (!keepSynthetic) recs = recs.filter((r) => !r.synthetic);
    return recs;
  }, [scopedRows, activeOnly, drillOn, drillMatch, status, visitsOnly, ordersOnly]);

  // KPI drill-down: each card toggles a table filter; the active card is ringed and clicking it clears.
  // Totals are computed from the full fetch above, so they don't change when a card is selected.
  const focus = {
    records: !activeOnly && !status && !anomalyOnly && !reviewOnly && !openOnly && !visitsOnly && !ordersOnly,
    present: status === 'PRESENT',
    absent: status === 'ABSENT',
    open: openOnly,
    anomaly: anomalyOnly,
    review: reviewOnly,
    visits: visitsOnly,
    orders: ordersOnly,
    pending: pendingOnly,
  };
  const clearFocus = () => {
    setActiveOnly(true); setStatus(undefined); setAnomalyOnly(false); setReviewOnly(false); setOpenOnly(false);
    setVisitsOnly(false); setOrdersOnly(false); setPendingOnly(false);
  };
  // A KPI drill-down applies its filter AND drops to the flat list so the filtered rows are visible.
  const setFocus = (apply: () => void) => {
    setActiveOnly(false); setStatus(undefined); setAnomalyOnly(false); setReviewOnly(false); setOpenOnly(false);
    setVisitsOnly(false); setOrdersOnly(false); setView('list'); apply();
  };

  /*
   * The reps behind "Customers not visited", which is the only thing that number CAN open.
   *
   * The figure is `book - covered` per rep, and `covered` counts every customer the rep actually
   * reached — including ones off their own book, a rule tried the strict way once and reverted
   * because it erased real work (PerformanceService.leaderboard says so at length). A difference of
   * two counts is not the size of a set, so there is no list of 2,265 named customers to open, and
   * the old drill-down pretended otherwise: it filtered the DAY table to rows carrying a non-zero
   * pending figure, which showed two punches for ten reps and answered nothing.
   *
   * Per rep it IS enumerable, and the parts add up to the tile exactly. So the tile opens the reps.
   */
  const pendingByRep = useMemo(() => {
    const m = new Map<string, { id: string; name: string; role: string; terrBook: TerritoryNode[]; book: number; covered: number; pending: number }>();
    for (const r of scopedRows as any[]) {
      const id = String(r.userId);
      if (m.has(id)) continue;
      m.set(id, {
        id,
        name: r.staffName ?? id,
        role: r.staffRole ?? '',
        terrBook: terr.booksOf(id),
        book: Number(r.customersOnBook ?? 0),
        covered: Number(r.periodCustomersCovered ?? 0),
        pending: Number(r.periodCustomersNotVisited ?? 0),
      });
    }
    return [...m.values()].filter((x) => x.pending > 0).sort((a, b) => b.pending - a.pending || a.name.localeCompare(b.name));
  }, [scopedRows, terr]);

  /*
   * The review backlog, and the gap between it and what this period can show.
   *
   * Two denominators, deliberately: the tab badge counts every day still waiting on a reviewer since
   * the system began, while the register shows one period. Left unsaid, that reads as a bug — a badge
   * of 8 over an Anomalies tile of 1 — and the seven days outside the window were unreachable, so the
   * badge counted work nobody could ever clear. So name the gap and give it a control.
   *
   * `reviewInWindow` is counted over the RAW window, before the search / territory narrowing, because
   * the sentence claims something about the PERIOD alone. For the same reason the note is withheld
   * while a staff or territory filter is on: the remainder would then also contain people the reader
   * deliberately filtered out, and "outside this period" would be a lie.
   */
  const reviewBacklog = (badges.attendanceAnomalies ?? 0) + (badges.attendanceIncomplete ?? 0);
  const reviewInWindow = useMemo(() => ((data ?? []) as any[]).filter(needsReview).length, [data]);
  const reviewOutside = Math.max(0, reviewBacklog - reviewInWindow);
  const unnarrowed = !userId && !search.trim() && !territoryIdOf(pick);
  // Open the whole backlog: widen the period back to its oldest day, then filter to exactly it.
  // Both writes land in the same tick — useUrlState coalesces them, so neither clobbers the other.
  const openReviewBacklog = () => {
    if (badges.attendanceReviewSince) setRange([dayjs(badges.attendanceReviewSince), dayjs()]);
    setFocus(() => setReviewOnly(true));
  };

  // Per-day rollup: every date in the range (newest first, capped at today), with status counts and the
  // day's rep rows for the expander. Sundays and declared holidays are labelled and never marked absent.
  const dayGroups = useMemo(() => {
    if (view !== 'byday') return [];
    const recs = scopedRows;
    const byDate = new Map<string, any[]>();
    for (const r of recs) { const a = byDate.get(r.logDate); if (a) a.push(r); else byDate.set(r.logDate, [r]); }
    const holName = new Map((holidays ?? []).map((h: any) => [String(h.holidayDate), h.name]));
    const start = range[0];
    const today = dayjs();
    let end = range[1];
    if (end.isAfter(today, 'day')) end = today; // no future rows to roll up
    const out: any[] = [];
    for (let d = end; !d.isBefore(start, 'day'); d = d.subtract(1, 'day')) {
      const ds = d.format('YYYY-MM-DD');
      // Sales Heads first, then Sales Persons — each block alphabetical — so the expander groups the
      // two roles instead of interleaving them.
      const rows = (byDate.get(ds) ?? []).slice().sort((a, b) =>
        (isHeadRole(b.staffRole) ? 1 : 0) - (isHeadRole(a.staffRole) ? 1 : 0)
        || String(a.staffName ?? '').localeCompare(String(b.staffName ?? '')));
      // A drill-down narrows the expander to the staff-days it picks, and drops days with none —
      // the same rows the List shows. The day's counts below stay whole-day totals.
      const shown = drillOn ? rows.filter(drillMatch) : rows;
      if (drillOn && !shown.length) continue;
      out.push({
        id: ds, date: ds, rows: shown, count: shown.length,
        isSunday: d.day() === 0,
        holiday: holName.get(ds) ?? null,
        present: rows.filter((r) => r.status === 'PRESENT').length,
        absent: rows.filter((r) => r.status === 'ABSENT').length,
        halfDay: rows.filter((r) => r.status === 'HALF_DAY').length,
        leave: rows.filter((r) => r.status === 'LEAVE').length,
        openShift: rows.filter((r) => r.checkInAt && !r.checkOutAt).length,
        workedMin: rows.reduce((s, r) => s + (Number(r.workedMin) || 0), 0), // total hours on the floor that day
        anomalies: rows.filter((r) => (r.anomalyFlags?.length ?? 0) > 0).length,
      });
    }
    return out;
  }, [view, scopedRows, holidays, range, drillOn, drillMatch]);

  // ── One staff row, described ONCE ───────────────────────────────────────────────────────────────
  // Both the flat list and the per-day expander render these columns, so switching views never loses
  // information. The list prepends a Date column; the expander inherits the date from its day row.
  const staffColumns: DataTableColumn<any>[] = [
    {
      // Wider than the other identity columns: the name now shares its line with a role badge, and
      // 180px truncated both to initials.
      key: 'staff', title: 'Staff', sortable: true, alwaysVisible: true, width: 240,
      render: (_: any, r: any) => (
        <StaffCell name={r.staffName} role={r.staffRole} userId={r.userId} book={terr.booksOf(r.userId)} />
      ),
    },
    { key: 'checkInAt', title: 'Check-in', dataIndex: 'checkInAt', sortable: true, width: 110, render: (v: string) => v ? formatIstTime(v) : '—' },
    { key: 'checkOutAt', title: 'Check-out', dataIndex: 'checkOutAt', sortable: true, width: 120, render: (v: string, r: any) => v ? formatIstTime(v) : (r.checkInAt ? <Tag tone="blue">Open</Tag> : '—') },
    {
      key: 'workedMin', title: 'Worked', dataIndex: 'workedMin', sortable: true, width: 95, align: 'right' as const,
      // Worked time is the SUM of the day's shifts, so a day with a break reads shorter than its
      // check-in to check-out — the shift count underneath says why.
      render: (v: number, r: any) => (
        <span className="inline-flex flex-col items-end">
          <span>{fmtWorked(v)}</span>
          {Number(r.sessionCount) > 1 && <span className="type-caption text-subtle">{r.sessionCount} shifts</span>}
        </span>
      ),
    },
    /* ── What the day PRODUCED ──────────────────────────────────────────────────────────────────
       Hours on the clock say nothing about output, so the register showed a punch pair and left the
       reader to go and look up the rep's day somewhere else. These three come from the server with
       the SAME definitions Sales Performance uses, so the two screens cannot disagree.
       A zero is a quiet dash, not a loud "0" — a column of zeros is noise, and the numbers that
       matter should be the ones the eye lands on. */
    {
      /* COMPLETED visits — the shared definition in common/analytics/visit-analytics.service.ts, the
         same number the performance dashboard and the coverage engine count. It used to show every
         visit with a check-in, which counted calls still in progress as done and disagreed with
         performance on the same rep's same day. Any still-open call is shown beside it, never inside
         it. */
      key: 'visitCount', title: 'Visits', dataIndex: 'visitsCompleted', sortable: true, width: 104, align: 'right' as const,
      render: (_: number, r: any) => {
        const done = Number(r.visitsCompleted ?? 0);
        const open = Math.max(0, Number(r.visitCount ?? 0) - done);
        if (!done && !open) return <span className="text-subtle">—</span>;
        return (
          <span className="inline-flex items-center gap-1">
            <Tooltip label={`${done} completed visit${done === 1 ? '' : 's'}${open ? `, ${open} still open` : ''}`}>
              <span className={cn('type-body-strong tabular-nums', done ? 'text-content' : 'text-subtle')}>{done || '—'}</span>
            </Tooltip>
            {open > 0 && (
              <TwTooltip label={`${open} visit(s) started but not closed — not counted as completed`}>
                <TwTag tone="blue">{open} open</TwTag>
              </TwTooltip>
            )}
          </span>
        );
      },
    },
    {
      /* How many DIFFERENT customers were reached ON THIS DATE. Three calls on one dealer is one
         customer seen, and the distinction is the whole point of a coverage-minded field day. This
         used to be served the rep's PERIOD total repeated on every row, so a day with no visits at
         all still showed customers against it. */
      key: 'customersVisited', title: 'Customers', dataIndex: 'customersVisited', sortable: true, width: 100, align: 'right' as const,
      render: (v: number) => (Number(v) > 0
        ? <span className="type-body-strong tabular-nums text-content">{v}</span>
        : <span className="text-subtle">—</span>),
    },
    {
      key: 'orderCount', title: 'Orders', dataIndex: 'orderCount', sortable: true, width: 150, align: 'right' as const,
      /*
       * Two numbers live in this cell and getting them to read right took three goes, so the reasoning
       * is worth keeping.
       *
       * "2  10 rej" was read as "2 accepted, 10 rejected". It is not: the count is every order NOT
       * rejected, so one still waiting at PLACED, one ON_HOLD and one already delivered all sit inside
       * it. Spelling the tag out to "10 rejected" did not help, and neither did a tooltip — the wrong
       * meaning is taken at a glance, before any hover. Labelling the count "2 standing" only moved the
       * question to what "standing" meant: an invented word explains nothing.
       *
       * What a reader actually does with this cell is reconstruct the day: HOW MANY were booked, and
       * how many survived. So the tag carries the total and the loss — "10 of 12 rejected" — and the
       * count stays a bare number, as on every other row. 12 minus 10 is the 2 above it, and the
       * arithmetic checks itself without a glossary.
       */
      render: (v: number, r: any) => {
        const kept = Number(v) || 0;
        const rejected = Number(r.ordersRejected) || 0;
        if (!kept && !rejected) return <span className="text-subtle">—</span>;
        const booked = kept + rejected;
        return (
          <span className="inline-flex flex-col items-end gap-1">
            <span className={cn('type-body-strong tabular-nums', kept ? 'text-content' : 'text-subtle')}>{kept || '—'}</span>
            {/* Refused business is neither counted nor hidden: a rep whose orders were all rejected
                must not look identical to one who booked nothing. */}
            {rejected > 0 && (
              <TwTooltip inline label={`This rep booked ${booked} order${booked === 1 ? '' : 's'} on this day and ${rejected} ${rejected === 1 ? 'was' : 'were'} rejected at approval, leaving the ${kept || 'none'} above — which is what the value beside it adds up. Cancelled and draft orders are in neither figure.`}>
                <TwTag tone="red">{rejected} of {booked} rejected</TwTag>
              </TwTooltip>
            )}
          </span>
        );
      },
    },
    {
      key: 'orderValue', title: 'Order value', dataIndex: 'orderValue', sortable: true, width: 120, align: 'right' as const,
      render: (v: number) => (Number(v) > 0
        ? <span className="type-body-strong tabular-nums text-content">{inr(v)}</span>
        : <span className="text-subtle">—</span>),
    },
    {
      /*
       * Book coverage — a PERIOD figure, and the only column here that is not about the row's date.
       * It is off by default and reads "2 / 116", never a bare number, because "114" in a day row
       * would be read as something that happened that day. It exists so the "Customers not visited"
       * tile can be traced to the rows it came from: without it that number was unsupported by
       * anything on screen, which is exactly how it came to look wrong.
       *
       * How many visits are OWED under each territory's norm is a different question with a
       * different answer — that one lives on Field Force → Visits & Coverage.
       */
      // "Visited", not "covered": covered means a met visit rule everywhere in the app (Visits & Coverage);
      // this is customers reached at least once.
      key: 'bookCoverage', title: 'Book visited', dataIndex: 'customersOnBook', sortable: true,
      defaultVisible: false, width: 116, align: 'right' as const,
      render: (_: any, r: any) => {
        const book = Number(r.customersOnBook ?? 0);
        if (!book) return <span className="text-subtle">—</span>;
        const covered = Number(r.periodCustomersCovered ?? 0);
        return (
          <TwTooltip label={`${covered} of ${book} customer(s) on this rep's book were visited between ${r.pendingFrom} and ${r.pendingTo}. Same on every row for this rep — it is a figure for the whole range, not for this date.`}>
            <span className="tabular-nums">
              <span className={cn('type-body-strong', covered ? 'text-content' : 'text-subtle')}>{covered}</span>
              <span className="text-subtle"> / {book}</span>
            </span>
          </TwTooltip>
        );
      },
    },
    /* The selfie's face-match SCORE. On-device face matching is not implemented yet (the app records
       the selfie and says so), so this is null on every real punch — hidden by default rather than
       removed, because the column becomes meaningful the day the matcher ships. */
    /*
     * "Selfie", not "Face" — was the raw `checkInFaceScore`, which printed 0.93 on every row that
     * had one because the web punch posted that constant with every check-in; a column whose value
     * never varies carries no information (see the record dialog for the full reasoning).
     *
     * What a reviewer can actually use from a list is whether a punch has a PHOTO behind it at all,
     * so that is what the column says. The verdict on the photo lives in the record.
     */
    { key: 'checkInSelfieUrl', title: 'Selfie', dataIndex: 'checkInSelfieUrl', sortable: true, defaultVisible: false, width: 88,
      render: (v: any, r: any) => (v
        // Blue, not green: a photo EXISTS is information, not a verdict. Nothing has matched it to
        // the rep yet, and a green chip would re-make the claim this column was built to retire.
        ? <TwTag tone="blue">Taken</TwTag>
        : <TwTooltip label={r?.checkInAt ? 'Punched in without a selfie — judge this day on its location evidence' : 'No punch-in on this day'}><span className="text-subtle">—</span></TwTooltip>) },
    {
      key: 'isWithinGeofence', title: 'Geofence', dataIndex: 'isWithinGeofence', sortable: true, width: 110, align: 'center' as const,
      render: (v: boolean, r: any) => !r.checkInAt ? '—' : v ? <Tag tone="green">In</Tag> : <Tag tone="red">Out</Tag>,
    },
    {
      // Where the rep last was that day — the latest activity (a punch or a visit), as a full address.
      key: 'lastLocation', title: 'Last location', dataIndex: 'lastActivityAt', width: 240, alwaysVisible: true,
      render: (_: any, r: any) => (
        <LastLocationCell
          lat={r.lastLat ?? r.checkOutLat ?? r.checkInLat}
          lng={r.lastLng ?? r.checkOutLong ?? r.checkInLong}
          type={r.lastActivityType ?? (r.checkOutAt ? 'Check-out' : r.checkInAt ? 'Check-in' : null)}
          at={r.lastActivityAt ?? r.checkOutAt ?? r.checkInAt}
        />
      ),
    },
    // Field MOVEMENT for this staff on this day — the GPS-trace route map (moved here from Sales Performance).
    ...(canRoute ? [{
      key: 'route', title: 'Route', width: 90,
      render: (_: any, r: any) => r.synthetic ? null : (
        <a
          className="inline-flex cursor-pointer items-center gap-1.5 text-brand-700 hover:text-brand-800 dark:text-brand-400"
          onClick={(e) => { e.stopPropagation(); setRouteRow({ id: String(r.userId), name: r.staffName ?? `#${r.userId}`, date: String(r.logDate) }); }}
        >
          <RouteIcon size={16} className="shrink-0" />
          Route
        </a>
      ),
    } as DataTableColumn<any>] : []),
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 200,
      render: (v: string, r: any) => (
        <Space size={4} wrap>
          <Tag tone={STATUS_COLOR[v] ?? 'neutral'}>{v}</Tag>
          {r.source === 'AUTO'
            ? <Tag tone="gold">Auto check-out{r.autoCheckoutIdleMin ? ` · idle ${fmtIdle(r.autoCheckoutIdleMin)}` : ''}</Tag>
            : (r.source && SOURCE_TAG[r.source] && <Tag tone={SOURCE_TAG[r.source].color}>{SOURCE_TAG[r.source].text}</Tag>)}
          {r.missingCheckout && <Tag tone="gold">Missing check-out</Tag>}
        </Space>
      ),
    },
    {
      // Chips only. The staff-history icon used to trail the chip inside this cell, so it landed at a
      // different x on every row — "OK" and "AUTO CHECKOUT" are not the same width — and the icons
      // zig-zagged down the page. Actions belong in the trailing action cluster, one fixed slot each.
      key: 'anomalyFlags', title: 'Anomalies', dataIndex: 'anomalyFlags', width: 140,
      render: (flags: string[] | undefined, r: any) => (
        <div className="flex flex-wrap items-center gap-1">
          {r.synthetic ? <span className="text-muted">—</span>
            : (flags ?? []).length > 0
              ? (flags ?? []).map((f) => {
                // Words, not the stored code — and red only for what is actually a doubt. "Punched
                // offline" is a fact about how the punch reached us, not something to answer for.
                const meta = attendanceFlag(f);
                return <TwTag key={f} tone={meta.tone}>{meta.label}</TwTag>;
              })
              : <TwTag tone="green">OK</TwTag>}
        </div>
      ),
    },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 84, align: 'right',
      render: (_: any, r: any) => (
        <div className="flex items-center justify-end gap-1" onClick={(e: any) => e.stopPropagation()}>
          <TwTooltip label="Attendance history">
            {/* Row click opens the day's detail drawer — this one opens the STAFF history instead,
                so it must not bubble or both drawers fire. */}
            <Button variant="text" size="sm" aria-label="Attendance history" icon={<CalendarClock size={16} />}
              onClick={(e: any) => { e?.stopPropagation?.(); setUserDrawer({ id: String(r.userId), name: r.staffName, role: r.staffRole }); }} />
          </TwTooltip>
          {/* Synthetic ABSENT rows have no stored record → nothing to open. The slot is still held
              open so the history icon above never slides right on those rows. */}
          {r.synthetic ? <span className="inline-block size-8" aria-hidden="true" /> : (
            <Tooltip label="View details">
              <Button variant="text" size="sm" aria-label="View details" icon={<Eye size={16} />}
                onClick={(e: any) => { e?.stopPropagation?.(); setDetailId(String(r.id)); }} />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const columns: DataTableColumn<any>[] = [
    // "15 Sep 2026", the date every other screen prints — the raw "2026-09-15" read like an import file.
    { key: 'logDate', title: 'Date', dataIndex: 'logDate', sortable: true, alwaysVisible: true, width: 120, render: (v: any) => <span className="whitespace-nowrap">{v ? formatIstDate(v) : '—'}</span> },
    ...staffColumns,
  ];

  // Filters shared by the flat list and the per-day view.
  const rangePicker = (
    <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])} allowClear={false} />
  );
  // Hidden for a rep: they cannot list staff (USER.VIEW), and the server only ever returns their own
  // attendance, so an always-empty "All staff" filter would just look broken.
  const staffPicker = canViewUsers ? (
    <Select
      placeholder="All staff" allowClear showSearch optionFilterProp="name" optionLabelProp="name"
      style={{ width: 220 }} value={userId} onChange={setUserId}
      options={staffOptions}
    />
  ) : null;
  const viewToggle = (
    <Segmented
      value={view} onChange={(v) => setView(v as 'list' | 'byday')}
      options={[{ label: 'List', value: 'list' }, { label: 'By day', value: 'byday' }]}
    />
  );
  const searchBox = (
    <Input
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search staff…" aria-label="Search staff"
      allowClear
      prefix={<Search size={14} className="text-subtle" />}
      style={{ width: 200 }}
    />
  );

  /* ── FILTERS ─────────────────────────────────────────────────────────────────────────────────
     Nine controls do not fit a toolbar row, so everything past the three you reach for every time
     (view, period, search) moves into the in-place panel, grouped by the COLUMN it acts on and in
     column order: Staff → Territory → Attendance. Territory is hidden outright when the actor can't
     read the territory masters — an always-empty State dropdown reads as a broken filter. */
  const clearAll = () => {
    setUserId(undefined); setSearch(''); setPick(NO_TERRITORY);
    setActiveOnly(true); setStatus(undefined); setAnomalyOnly(false); setReviewOnly(false); setOpenOnly(false);
    setVisitsOnly(false); setOrdersOnly(false); setPendingOnly(false);
  };
  /* Each section takes an IDENTITY HUE and a glyph — four grey headings in a row meant reading all
     four every time to find the one you wanted. The hues are from the identity ramp (no red, no
     brown): they say WHICH section this is, never that anything is wrong. */
  const filterGroups: FilterGroup[] = [
    {
      title: 'Staff',
      tone: PALETTE.sky[500],
      icon: <Users size={13} />,
      fields: [
        ...(canViewUsers ? [{
          label: 'Person',
          control: (
            <Select placeholder="All staff" allowClear showSearch optionFilterProp="name" optionLabelProp="name"
              value={userId} onChange={setUserId} options={staffOptions} />
          ),
        }] : []),
      ],
    },
    /* Territory is a CASCADE, not three peers: District appears once a state is chosen and Beat once
       a district is, so the panel never offers a 400-entry beat list that means nothing on its own,
       and a district can't be picked under the wrong state. "All states" / "All districts" are real
       options rather than a clear-X, because widening the filter should be as visible as narrowing
       it — and choosing one collapses the level(s) below back out of the panel.

       Every option carries its STAFF COUNT — "Maharashtra (9)" — and a place with nobody in it is
       DISABLED and sorted to the bottom rather than hidden: you can see where you have no field
       staff without being able to filter yourself into an empty table. Same treatment as the Sales
       Performance cascade, so the two pages behave identically. */
    ...(terr.available ? [{
      title: 'Territory',
      tone: PALETTE.brand[500],
      icon: <MapPin size={13} />,
      fields: [
        {
          label: 'State',
          control: (
            <Select showSearch optionFilterProp="label"
              value={pick.state ?? ALL_T}
              // Picking a state strands a district/beat chosen under a different one — clear the
              // levels below rather than leaving a combination that matches nobody.
              onChange={(v: any) => setPick({ state: v === ALL_T ? null : String(v), district: null, beat: null })}
              options={[{ value: ALL_T, label: 'All states' }, ...terr.optionsAt('state', null)]} />
          ),
        },
        ...(pick.state ? [{
          label: 'District',
          control: (
            <Select showSearch optionFilterProp="label"
              value={pick.district ?? ALL_T}
              onChange={(v: any) => setPick({ ...pick, district: v === ALL_T ? null : String(v), beat: null })}
              options={[{ value: ALL_T, label: 'All districts' }, ...terr.optionsAt('district', pick.state)]} />
          ),
        }] : []),
        ...(pick.district ? [{
          label: 'Beat',
          control: (
            <Select showSearch optionFilterProp="label"
              value={pick.beat ?? ALL_T}
              onChange={(v: any) => setPick({ ...pick, beat: v === ALL_T ? null : String(v) })}
              options={[{ value: ALL_T, label: 'All beats' }, ...terr.optionsAt('beat', pick.district)]} />
          ),
        }] : []),
      ],
    } as FilterGroup] : []),
    {
      title: 'Attendance',
      tone: PALETTE.plum[400],
      icon: <CalendarDays size={13} />,
      fields: [
        {
          label: 'Status',
          control: (
            <Select placeholder="Any status" allowClear disabled={activeOnly} value={status}
              onChange={(v: any) => (v ? setFocus(() => setStatus(v)) : clearFocus())}
              /* The dot is the status tag's OWN hue (STATUS_COLOR is what the table's tags read), so
                 picking "ABSENT" here and finding red rows is one colour, not two. A status whose tag
                 is grey everywhere — HOLIDAY — gets the hollow ring rather than a made-up colour. */
              options={STATUS_OPTIONS.map((v) => hueOption(
                v, v.replace('_', ' '),
                STATUS_COLOR[v] === 'neutral' ? undefined : TAG_TONE_HUE[STATUS_COLOR[v] ?? 'neutral'],
              ))} />
          ),
          hint: activeOnly ? 'Showing worked days only — switch Records to “All” to pick a status.' : undefined,
        },
        {
          label: 'Records',
          control: (
            <Select value={activeOnly ? 'worked' : 'all'}
              onChange={(v: any) => (v === 'worked' ? clearFocus() : setFocus(() => {}))}
              options={[
                hueOption('worked', 'Worked days only', TAG_TONE_HUE.green),
                hueOption('all', 'All records'),
              ]} />
          ),
        },
        {
          label: 'Review',
          control: (
            <Select value={reviewOnly ? 'review' : anomalyOnly ? 'only' : 'any'}
              onChange={(v: any) => (v === 'only' ? setFocus(() => setAnomalyOnly(true))
                : v === 'review' ? setFocus(() => setReviewOnly(true))
                  : clearFocus())}
              options={[
                hueOption('any', 'Any'),
                // Red is what a flag wears in the Anomalies column; gold is the "Needs review" tile.
                hueOption('only', 'With anomalies', TAG_TONE_HUE.red),
                hueOption('review', 'Needs review (flagged or half-punched)', TAG_TONE_HUE.gold),
              ]} />
          ),
        },
        {
          label: 'Shift',
          control: (
            <Select value={openOnly ? 'open' : 'any'}
              onChange={(v: any) => (v === 'open' ? setFocus(() => setOpenOnly(true)) : clearFocus())}
              options={[
                hueOption('any', 'Any'),
                // Blue, matching the "Open" tag in the Check-out column and the Open shifts tile.
                hueOption('open', 'Open (no check-out)', TAG_TONE_HUE.blue),
              ]} />
          ),
        },
      ],
    },
    /* Output — the same two questions the Visits and Orders tiles answer, available here so they can
       be combined with a territory or a person instead of only as a tile drill-down. */
    {
      title: 'Output',
      tone: PALETTE.harvest[400],
      icon: <PackageCheck size={13} />,
      fields: [
        {
          label: 'Visits',
          control: (
            <Select value={visitsOnly ? 'only' : 'any'}
              onChange={(v: any) => (v === 'only' ? setFocus(() => setVisitsOnly(true)) : clearFocus())}
              options={[hueOption('any', 'Any'), hueOption('only', 'Made a visit', TAG_TONE_HUE.blue)]} />
          ),
        },
        {
          label: 'Orders',
          control: (
            <Select value={ordersOnly ? 'only' : 'any'}
              onChange={(v: any) => (v === 'only' ? setFocus(() => setOrdersOnly(true)) : clearFocus())}
              options={[hueOption('any', 'Any'), hueOption('only', 'Booked an order', TAG_TONE_HUE.gold)]} />
          ),
        },
      ],
    },
  ].filter((g) => g.fields.length > 0);

  const staffLabel = (id?: string) =>
    staffOptions.flatMap((g) => g.options).find((o) => o.value === id)?.name ?? id;
  const filterChips: FilterChip[] = [
    userId ? { key: 'user', label: `Staff: ${staffLabel(userId)}`, onRemove: () => setUserId(undefined) } : null,
    search.trim() ? { key: 'q', label: `“${search.trim()}”`, onRemove: () => setSearch('') } : null,
    pick.state ? { key: 'state', label: `State: ${terr.nameOf(pick.state)}`, onRemove: () => setPick(NO_TERRITORY) } : null,
    pick.district ? { key: 'district', label: `District: ${terr.nameOf(pick.district)}`, onRemove: () => setPick({ ...pick, district: null, beat: null }) } : null,
    pick.beat ? { key: 'beat', label: `Beat: ${terr.nameOf(pick.beat)}`, onRemove: () => setPick({ ...pick, beat: null }) } : null,
    status ? { key: 'status', label: `Status: ${status.replace('_', ' ')}`, onRemove: () => setStatus(undefined) } : null,
    !activeOnly ? { key: 'records', label: 'All records', onRemove: () => setActiveOnly(true) } : null,
    anomalyOnly ? { key: 'anom', label: 'With anomalies', onRemove: () => setAnomalyOnly(false) } : null,
    reviewOnly ? { key: 'review', label: 'Needs review', onRemove: () => setReviewOnly(false) } : null,
    openOnly ? { key: 'open', label: 'Open shifts', onRemove: () => setOpenOnly(false) } : null,
    visitsOnly ? { key: 'visits', label: 'Made a visit', onRemove: () => setVisitsOnly(false) } : null,
    ordersOnly ? { key: 'orders', label: 'Booked an order', onRemove: () => setOrdersOnly(false) } : null,
  ].filter(Boolean) as FilterChip[];

  const filters = useFilterPanel({ groups: filterGroups, chips: filterChips, onClear: clearAll });

  /* ── What the EXPORT is ─────────────────────────────────────────────────────────────────────────
     The dialog used to ask for a period and a staff tick-list of its OWN, on top of the nine filters
     already sitting on this page. Two ways to say one thing: the file could quietly disagree with the
     screen that produced it, and nothing in it said which of the two scopes had won. It takes this
     page's live filters now — period, person, status, the review / shift / output narrowing and the
     column sort — and the dialog prints them back rather than asking for them again.

     The name search and the territory cascade are resolved HERE and travel as ids: a rep's territory
     book lives in the browser (`useStaffTerritories`), so the server cannot re-derive who is in view.
     `'0'` is a user id that exists nowhere — a narrowing that matches nobody has to export nothing,
     not everybody. */
  const exportPeople = useMemo(
    () => [...new Set(scopedRows.map((r: any) => String(r.userId)))],
    [scopedRows],
  );
  const peopleNarrowed = !!search.trim() || !!territoryIdOf(pick);
  const exportParams = useMemo(() => {
    const p: Record<string, string> = {
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
      absent: '1',
    };
    if (userId) p.userId = userId;
    if (peopleNarrowed) p.userIds = exportPeople.join(',') || '0';
    if (status) p.status = status;
    if (activeOnly) p.active = '1';
    if (anomalyOnly) p.anomaly = '1';
    if (reviewOnly) p.review = '1';
    if (openOnly) p.open = '1';
    if (visitsOnly) p.visits = '1';
    if (ordersOnly) p.orders = '1';
    if (sort) p.sort = sort;
    return p;
  }, [range, userId, peopleNarrowed, exportPeople, status, activeOnly, anomalyOnly, reviewOnly,
    openOnly, visitsOnly, ordersOnly, sort]);

  // The sort in words. `-workedMin` is the wire form; the reader knows the column by its heading, and
  // ascending / descending is the direction they just clicked into it.
  const sortLabel = sortState
    ? `${columns.find((c) => c.key === sortState.key)?.title ?? sortState.key}, ${sortState.dir === 'asc' ? 'ascending' : 'descending'}`
    : 'Newest day first';
  /* `activeOnly` is this page's DEFAULT and so carries no chip — but it is the filter that decides
     whether a timesheet holds absences, which is the first thing a payroll reader checks. Said out
     loud here, because "Filters: None" over a file of worked days only would be untrue. */
  const exportFilterText = [
    ...(activeOnly ? ['Worked days only'] : []),
    ...filterChips.map((c) => c.label).filter((l): l is string => typeof l === 'string'),
  ];
  const exportScope = (
    <div className="space-y-1">
      <ScopeRow label="Period">
        {range[0].format('DD MMM YYYY')} – {range[1].format('DD MMM YYYY')} · {windowDays} days
      </ScopeRow>
      <ScopeRow label="Staff" tone={peopleNarrowed && !exportPeople.length ? 'text-warning-600' : undefined}>
        {userId ? staffLabel(userId)
          : !peopleNarrowed ? 'Everyone in your scope'
            : exportPeople.length ? `${exportPeople.length} staff left by the search / territory`
              // Named here rather than left to be discovered in an empty spreadsheet.
              : 'Nobody matches these filters — this file will have no rows'}
      </ScopeRow>
      <ScopeRow label="Filters">{exportFilterText.length ? exportFilterText.join(' · ') : 'None'}</ScopeRow>
      <ScopeRow label="Order">{sortLabel}</ScopeRow>
      {/* The unit, because every count on this page is a STAFF-DAY. The By-day tab shows one row per
          DATE, so "the list below" would be a different thing there — the file is the flat timesheet
          either way. */}
      <ScopeRow label="Rows">
        {tableRows.length.toLocaleString('en-IN')} staff-days —{' '}
        {view === 'byday' ? 'the timesheet behind the day summary below' : 'exactly what the list below is showing'}
      </ScopeRow>
    </div>
  );
  // The modal does double duty: mark attendance ON BEHALF (punch correction — FIELD.ONBEHALF, SA-only)
  // and mark LEAVE across a range (ATTENDANCE.MANAGE — a roster function admins keep). Show the button
  // to whoever can do either; the modal restricts the statuses to what the actor is allowed.
  const markBtn = (canOnBehalf || canManage) ? (
    <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>Mark attendance</Button>
  ) : undefined;

  // Chain into coverage only when the leave reaches today/future AND the SP holds territories AND
  // the marker can actually grant coverage. Coverage starts no earlier than today (can't cover the past).
  const handleLeaveMarked = (res: any, ctx: { userId: string; from: string; to: string }) => {
    if (!canCoverage || !res?.touchesTodayOrFuture || (res?.territoryCount ?? 0) === 0) return;
    const todayS = dayjs().format('YYYY-MM-DD');
    setCoveragePrompt({ absentId: String(ctx.userId), from: ctx.from < todayS ? todayS : ctx.from, to: ctx.to });
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Attendance</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canExport && (
            <Button size="sm" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
          )}
          {markBtn}
        </div>
      </div>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Daily check-in / check-out with GPS, selfie and anomaly detection.
      </Typography.Paragraph>

      <Tabs
        activeKey={section}
        onChange={setSection}
        items={[
          {
            key: 'log',
            label: (
              <Space size={6}>
                Attendance log
                {/*
                  * The badge counts the whole review backlog, but opening the tab never applies a filter —
                  * pressing it used to land on a pre-filtered "Needs review" list, which read as missing
                  * attendance. The backlog opens from the "Needs review" tile or the "Review all" button.
                  */}
                <Tooltip label={`Days still waiting on a reviewer — flagged punches plus past days missing a check-in or check-out. Counted since the system began, not for the period below${badges.attendanceReviewSince ? `; the oldest is ${dayjs(badges.attendanceReviewSince).format('DD MMM YYYY')}` : ''}.`}>
                  <Badge count={reviewBacklog} size="small" overflowCount={99} />
                </Tooltip>
              </Space>
            ),
            children: (
              <>
                {/*
                  The unit, named. Every tile below counts STAFF-DAYS -- one person on one day -- but
                  the band sits under a date range, so "Present 33" across 18 Jul - 11 Sep was being
                  read as "33 of those 56 days", which is a different and much worse number. Saying
                  the unit and its total once, here, costs one line and fixes all eight tiles.

                  It also says which days are countable at all: an absence is only raised for a
                  working day, so Sundays and company holidays are missing from the total rather
                  than counted as misses -- otherwise that gap has to be reverse-engineered.
                */}
                {kpis.staffDays > 0 && (
                  <div className="mb-2 type-label text-subtle">
                    Every tile counts <span className="font-semibold text-content">staff-days</span> — one person on one day, not calendar days.{' '}
                    {kpis.staffDays.toLocaleString('en-IN')} in view, from {kpis.reps} staff member{kpis.reps === 1 ? '' : 's'} over{' '}
                    {windowDays} calendar day{windowDays === 1 ? '' : 's'}; Sundays and company holidays are not counted.
                  </div>
                )}
                <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {/* One hue per meaning, so the row can be read at a glance instead of by label:
                      Records is the unfiltered denominator (deliberately quiet), Present is the
                      good outcome, Absent the bad one, Open shift an in-progress/informational
                      state, Needs review the reviewer's queue, and the last two are OUTPUT —
                      what the hours on the clock actually produced.

                      Every tile carries a second line saying what its number is made of and what
                      clicking it will show, because a bare figure on a band of seven leaves the
                      reader guessing at both. */}
                  <Kpi
                    title="Records" value={kpis.records} tone="neutral" icon={<Users size={16} />}
                    hint="Staff-days with a row on the log"
                    active={focus.records} onClick={() => (focus.records ? clearFocus() : setFocus(() => {}))}
                  />
                  <Kpi
                    title="Present" value={kpis.present} tone="brand" icon={<CheckCircle2 size={16} />}
                    hint="Staff-days punched in"
                    active={focus.present} onClick={() => (focus.present ? clearFocus() : setFocus(() => setStatus('PRESENT')))}
                  />
                  <Kpi
                    title="Absent" value={kpis.absent} tone="danger" icon={<UserX size={16} />}
                    hint="Marked absent, or never punched in"
                    active={focus.absent} onClick={() => (focus.absent ? clearFocus() : setFocus(() => setStatus('ABSENT')))}
                  />
                  <Kpi
                    title="Open shifts" value={kpis.openShift} tone="sky" icon={<Clock size={16} />}
                    hint="Punched in, never punched out"
                    dead={kpis.openShift === 0}
                    active={focus.open} onClick={() => (focus.open ? clearFocus() : setFocus(() => setOpenOnly(true)))}
                  />
                  {/*
                    Named for the QUEUE, not for one of the two things in it. "Anomalies" over a note
                    reading "7 more need review" and a button reading "Review all 8" was three names
                    for one number, and the reader was left to guess whether 1 and 8 were even the
                    same currency. The tile now opens exactly what it counts, too — the review slice,
                    not the narrower flags-only one, which used to show fewer rows than the tile said.
                  */}
                  <Kpi
                    title="Needs review" value={kpis.anomalies} tone="warning" icon={<AlertTriangle size={16} />}
                    hint={reviewOutside > 0 && unnarrowed
                      ? `${kpis.anomalies} in this period · ${reviewOutside} older`
                      : 'Staff-days waiting on a reviewer'}
                    dead={kpis.anomalies === 0}
                    active={focus.review} onClick={() => (focus.review ? clearFocus() : setFocus(() => setReviewOnly(true)))}
                  />
                  {/* ── Output. The value each tile shows is the WORK DONE; the hint says how many
                      days it came from, which is what the drill-down will actually list. ── */}
                  <Kpi
                    title="Visits" value={kpis.visits} tone="sky" icon={<Store size={16} />}
                    hint={kpis.visitDays ? `on ${kpis.visitDays} staff-day${kpis.visitDays === 1 ? '' : 's'} · click to show them` : 'No visits in view'}
                    dead={kpis.visitDays === 0}
                    active={focus.visits} onClick={() => (focus.visits ? clearFocus() : setFocus(() => setVisitsOnly(true)))}
                  />
                  <Kpi
                    title="Orders" value={kpis.orders} tone="gold" icon={<PackageCheck size={16} />}
                    hint={kpis.orders
                      ? `${inr(kpis.orderValue)} on ${kpis.orderDays} staff-day${kpis.orderDays === 1 ? '' : 's'}${kpis.rejected ? ` · ${kpis.rejected} more rejected` : ''}`
                      : 'No orders booked in view'}
                    dead={kpis.orderDays === 0}
                    active={focus.orders} onClick={() => (focus.orders ? clearFocus() : setFocus(() => setOrdersOnly(true)))}
                  />
                  {/*
                    The one PERIOD figure on a band of day figures, and the only one whose evidence is
                    a rep rather than a row — so it says so in its own words.

                    It was called "Visits pending", which was wrong twice over. It counts CUSTOMERS,
                    not visits. And "visits pending" is already the name of a different number on
                    Visits & Coverage (calls still owed against each territory's norm), so the two
                    screens showed different figures under one label — 2,259 here against 2,103 there
                    — with nothing on either page to explain the gap. Clicking it also filtered to
                    reps who still have customers to reach, while the column that carried the figure
                    had been removed from the table, leaving a number with no visible support at all.
                  */}
                  <Kpi
                    title="Customers not visited" value={kpis.pending.toLocaleString('en-IN')} tone="warning" icon={<Hourglass size={16} />}
                    hint={kpis.book
                      ? `of ${kpis.book.toLocaleString('en-IN')} customers on ${kpis.reps} rep book${kpis.reps === 1 ? '' : 's'} · whole date range · click for the ${kpis.pendingReps} rep${kpis.pendingReps === 1 ? '' : 's'} behind`
                      : 'No customers on these books'}
                    dead={kpis.pending === 0}
                    active={focus.pending} onClick={() => setPendingOnly(!pendingOnly)}
                  />
                </div>
                {/*
                  The badge above counts the whole backlog; this band counts the period. When those
                  disagree, say by how much and offer the way across — otherwise the older days are
                  counted forever and reachable never, which is exactly what a reviewer would have
                  found here: a badge of 8 above a week holding one of them.
                */}
                {reviewOutside > 0 && unnarrowed && (
                  <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-warning-500/25 bg-warning-500/10 px-3.5 py-2.5">
                    <AlertTriangle size={16} className="shrink-0 text-warning-600" />
                    <span className="min-w-0 type-body text-content">
                      <strong className="type-body-strong">{reviewOutside}</strong>
                      {reviewOutside === 1 ? ' more staff-day needs' : ' more staff-days need'} review outside this period
                      {badges.attendanceReviewSince && ` — the oldest is ${dayjs(badges.attendanceReviewSince).format('DD MMM YYYY')}`}.
                    </span>
                    <Button size="sm" className="ml-auto" onClick={openReviewBacklog}>
                      Review all {reviewBacklog}
                    </Button>
                  </div>
                )}
                {view === 'list' ? (
                  <Card>
                    <DataTable
                      storageKey="attendance"
                      rowKey="id"
                      layoutFixed
                      loading={isLoading}
                      dataSource={tableRows}
                      columns={columns}
                      sortState={sortState}
                      onSortChange={onSortChange}
                      // The whole row opens the detail drawer — the eye button stays for
                      // discoverability. Synthetic (absent) rows have no record to open.
                      onRow={(r: any) => (r.synthetic ? undefined : { onClick: () => setDetailId(String(r.id)) })}
                      pagination={{ pageSize: 25, showSizeChanger: true }}
                      toolbarLeft={
                        <>
                          {viewToggle}
                          {rangePicker}
                          {searchBox}
                          {filters.trigger}
                          {filters.chips}
                        </>
                      }
                      toolbarBelow={filters.panel}
                    />
                  </Card>
                ) : (
                  <Card>
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      {viewToggle}
                      {rangePicker}
                      {searchBox}
                      {filters.trigger}
                      {filters.chips}
                    </div>
                    {filters.panel}
                    <DailySummary groups={dayGroups} loading={isLoading} staffColumns={staffColumns} />
                  </Card>
                )}
              </>
            ),
          },
          ...(canApproveReg ? [{
            key: 'reg',
            label: <Space size={6}><RefreshCw size={16} /> Regularization<Badge count={badges.regularizationsPending ?? 0} size="small" overflowCount={99} /></Space>,
            children: <RegularizationReviewCard />,
          }] : []),
          ...(canManage ? [{
            key: 'holidays',
            label: <Space size={6}><CalendarDays size={16} /> Holidays</Space>,
            children: <HolidaysCard />,
          }] : []),
        ]}
      />

      {/*
        What "Customers not visited" opens. One row per rep, and the rows ADD UP to the tile — which
        the old behaviour could not do, because it filtered a table of staff-days with a figure that
        counts customers.

        The closing note is not decoration. The number is `book - covered`, and `covered` credits
        every customer the rep reached, including ones off their own book. So it is a difference of
        two counts rather than the size of a set, and no screen can list it customer by customer
        without quietly changing the metric — which is why the reader has to be sent to Visits &
        Coverage instead of being given a drill-down here.

        That reasoning stays in this comment. What the NOTE says is a different job: the reader is a
        sales manager who wants the names of the shops, so it opens with where to get them and gives
        the reason in one plain sentence afterwards. It used to open with "Why there is no
        customer-by-customer list here" and explain the metric as set arithmetic, which answers a
        question nobody asked in words nobody outside this file uses.
      */}
      <Modal open={pendingOnly} onOpenChange={(o) => { if (!o) setPendingOnly(false); }} width={860}
        title="Customers not visited — by rep"
        description={`${kpis.pending.toLocaleString('en-IN')} of ${kpis.book.toLocaleString('en-IN')} customers went unreached between ${range[0].format('DD MMM YYYY')} and ${range[1].format('DD MMM YYYY')}.`}
        footer={<Button variant="primary" onClick={() => setPendingOnly(false)}>Close</Button>}>
        {pendingByRep.length === 0 ? (
          <EmptyState title="Every book was visited" description="No rep in view has a customer left unreached in this period." />
        ) : (
          <>
            <DataTable
              rowKey="id"
              storageKey="att-pending-by-rep"
              /* a column-picker gear floating over a nine-row dialog is noise */
              showToolbar={false}
              dataSource={pendingByRep}
              pagination={false}
              layoutFixed
              columns={[
                {
                  key: 'name', title: 'Staff', dataIndex: 'name', width: 300,
                  render: (_: any, r: any) => (
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="type-body-strong text-content">{r.name}</span>
                        {r.role ? <RoleBadge role={r.role} /> : null}
                      </div>
                      <TerritoryNodeChips nodes={r.terrBook ?? []} max={3} hideWhenEmpty />
                    </div>
                  ),
                },
                { key: 'book', title: 'On book', dataIndex: 'book', align: 'right' as const, width: 110,
                  render: (v: number) => <span className="tabular-nums">{v.toLocaleString('en-IN')}</span> },
                { key: 'covered', title: 'Reached', dataIndex: 'covered', align: 'right' as const, width: 110,
                  render: (v: number) => <span className="tabular-nums text-brand-600">{v.toLocaleString('en-IN')}</span> },
                { key: 'pending', title: 'Not visited', dataIndex: 'pending', align: 'right' as const, width: 130,
                  render: (v: number) => <span className="tabular-nums type-body-strong text-warning-600">{v.toLocaleString('en-IN')}</span> },
              ] as DataTableColumn<any>[]}
            />
            {/* The total, stated — so the reader can see the parts are the whole and not a sample. */}
            <div className="mt-2 flex items-center justify-between gap-3 rounded-card bg-surface-2 px-3.5 py-2.5">
              <span className="type-label text-subtle">
                {pendingByRep.length} rep{pendingByRep.length === 1 ? '' : 's'} behind, of {kpis.reps} in view
              </span>
              <span className="type-body-strong tabular-nums text-content">{kpis.pending.toLocaleString('en-IN')} not visited</span>
            </div>
            {/* Reads as HELP, not as a disclaimer: the thing the reader wants comes first, and the
                reason follows in one plain sentence. The old note opened with "Why there is no
                customer-by-customer list here" and explained the metric as set arithmetic — "the
                difference is a count, not a named set" — which tells somebody looking for a list of
                shops that they have hit a wall, in vocabulary they have no use for. The engineering
                reason is worth keeping; it belongs in the comment above, not on the screen. */}
            <p className="mt-3 type-label text-subtle">
              <b className="font-semibold text-content">Want to see which customers?</b>{' '}
              Open{' '}
              <a className="font-semibold text-brand-600 underline underline-offset-2" href="/ffa/visits?section=coverage">Visits &amp; Coverage</a>
              {' '}— it lists them by territory, with when each one is next due.
              {' '}Reps often cover for each other, so a customer can be reached by a rep whose book they are
              not on. That is why this screen counts customers per rep rather than naming them.
            </p>
          </>
        )}
      </Modal>

      <AttendanceDetailModal id={detailId} onClose={() => setDetailId(null)} canManage={canManage} />
      <AttendanceUserDrawer user={userDrawer} onClose={() => setUserDrawer(null)} />
      {routeRow && <RouteMapModal spId={routeRow.id} name={routeRow.name} from={routeRow.date} to={routeRow.date} onClose={() => setRouteRow(null)} />}
      {/* No period of its own and no staff tick-list: the file takes THIS page's filters, the column
          sort included, so the two cannot disagree. A dialog with nothing left to set would say
          nothing about what it is about to download, so the scope is printed instead. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export attendance"
        description="A timesheet of exactly what this page is showing. Change the filters behind this dialog to change the file."
        baseUrl="/attendance/export"
        baseParams={exportParams}
        hidePeriod
        scopeSummary={exportScope}
      />
      <MarkAttendanceModal open={createOpen} onClose={() => setCreateOpen(false)} staffOptions={staffOptions} onLeaveMarked={handleLeaveMarked} canOnBehalf={canOnBehalf} canManage={canManage} />
      <GrantCoverageModal
        open={!!coveragePrompt}
        onClose={() => setCoveragePrompt(null)}
        presetAbsentId={coveragePrompt?.absentId}
        presetValidFrom={coveragePrompt?.from}
        presetValidTo={coveragePrompt?.to}
      />
    </div>
  );
}

/**
 * Per-day rollup: one row per date with status counts; expand a day to see its rep rows.
 * The expander renders the SAME staff columns as the flat list (geofence, last location, route,
 * source/auto-checkout chips, history, details…) so the two views never disagree on what's shown.
 */
function DailySummary({ groups, loading, staffColumns }: { groups: any[]; loading: boolean; staffColumns: DataTableColumn<any>[] }) {
  const dayRank = (g: any) => (g.holiday ? 2 : g.isSunday ? 1 : 0);
  const dayTag = (g: any) =>
    g.holiday ? <Tag tone="gold">Holiday · {g.holiday}</Tag>
      : g.isSunday ? <Tag>Weekly off</Tag>
        : <Tag tone="green">Working</Tag>;
  const num = (v: number, color?: string) =>
    v ? <span style={{ fontWeight: 600, color }}>{v}</span> : <span style={{ color: '#bbb' }}>—</span>;

  return (
    <Table
      size="small"
      rowKey="id"
      loading={loading}
      dataSource={groups}
      pagination={{ pageSize: 31, hideOnSinglePage: true }}
      locale={{ emptyText: <EmptyState title="No days in range" /> }}
      columns={[
        // Every column sorts both ways (click once for ascending, again for descending, a third time
        // to fall back to the calendar order the days arrive in — newest day first).
        { title: 'Date', dataIndex: 'date', sorter: true, render: (v: string) => <span style={{ fontWeight: 500 }}>{dayjs(v).format('ddd, DD MMM YYYY')}</span> },
        // Sorting by day KIND groups the working days together, so it ranks them rather than comparing
        // the rendered tag text: working day → weekly off → holiday.
        { title: 'Day', key: 'day', sorter: (a: any, b: any) => dayRank(a) - dayRank(b), render: (_: any, g: any) => dayTag(g) },
        { title: 'Present', dataIndex: 'present', align: 'right' as const, sorter: true, render: (v: number) => num(v, PALETTE.brand[600]) },
        { title: 'Absent', dataIndex: 'absent', align: 'right' as const, sorter: true, render: (v: number) => num(v, PALETTE.danger[600]) },
        { title: 'Half-day', dataIndex: 'halfDay', align: 'right' as const, sorter: true, render: (v: number) => num(v, '#B8860B') },
        { title: 'Leave', dataIndex: 'leave', align: 'right' as const, sorter: true, render: (v: number) => num(v, PALETTE.sky[500]) },
        { title: 'Open', dataIndex: 'openShift', align: 'right' as const, sorter: true, render: (v: number) => num(v, PALETTE.sky[500]) },
        { title: 'Worked', dataIndex: 'workedMin', align: 'right' as const, sorter: true, render: (v: number) => (v ? <span style={{ fontWeight: 600 }}>{fmtWorked(v)}</span> : <span style={{ color: '#bbb' }}>—</span>) },
        { title: 'Anomalies', dataIndex: 'anomalies', align: 'right' as const, sorter: true, render: (v: number) => num(v, PALETTE.warning[600]) },
      ]}
      expandable={{
        rowExpandable: (g: any) => g.count > 0,
        // The whole day row opens its staff-days — the chevron marks that it can, it isn't the
        // only target. Nothing in the day row is itself clickable, so there is nothing to steal.
        expandRowByClick: true,
        expandedRowRender: (g: any) => (
          <DataTable
            storageKey="attendance-byday-staff"
            rowKey="id"
            initialDensity="small"
            showToolbar={false}
            dataSource={g.rows}
            columns={staffColumns}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
          />
        ),
      }}
    />
  );
}

function MarkAttendanceModal({ open, onClose, staffOptions, onLeaveMarked, canOnBehalf, canManage }: {
  open: boolean; onClose: () => void;
  staffOptions: { label: string; options: { value: string; name: string; label: ReactNode }[] }[];
  onLeaveMarked?: (res: any, ctx: { userId: string; from: string; to: string }) => void;
  canOnBehalf?: boolean; canManage?: boolean;
}) {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const statusVal = Form.useWatch('status', form);
  const isLeave = statusVal === 'LEAVE';
  // On-behalf punch statuses need FIELD.ONBEHALF; LEAVE (a roster action) needs ATTENDANCE.MANAGE.
  // So an on-behalf-only actor sees no LEAVE, and a manage-only actor sees ONLY leave.
  const statusOptions = STATUS_OPTIONS.filter((s) => (s === 'LEAVE' ? canManage : canOnBehalf));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['attendance'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };

  const save = useConfirmedMutation({ title: "Save this attendance?", content: "It is marked on behalf of the staff member and recorded against your name.", okText: "Yes, save" }, {
    mutationFn: (vals: any) => request({
      url: '/attendance/admin', method: 'POST',
      data: {
        userId: vals.userId,
        logDate: vals.logDate.format('YYYY-MM-DD'),
        status: vals.status,
        checkInAt: vals.checkInAt ? vals.checkInAt.toISOString() : undefined,
        checkOutAt: vals.checkOutAt ? vals.checkOutAt.toISOString() : undefined,
        note: vals.note,
        onBehalfReason: vals.note, // required + audited for on-behalf punch corrections
      },
    }),
    onSuccess: () => {
      message.success('Attendance saved');
      invalidate();
      form.resetFields();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  // LEAVE is marked across a range (future allowed); the server skips Sundays + applicable holidays.
  const leaveMut = useConfirmedMutation({ title: "Mark leave for this date range?", content: "Every day in the range is marked as leave for this staff member.", okText: "Yes, mark leave" }, {
    mutationFn: (body: { userId: string; from: string; to: string; note?: string }) =>
      request<any>({ url: '/attendance/admin/leave', method: 'POST', data: body }),
    onSuccess: (res: any, body) => {
      const w = res?.written?.length ?? 0;
      const skipped: any[] = res?.skipped ?? [];
      // A day the rep actually worked — punched in, or made visits — is never turned into leave.
      const worked = skipped.filter((s) => s.reason === 'worked').length;
      const off = skipped.length - worked;
      const ov = res?.overwritten?.length ?? 0;
      const notes = [
        off ? `skipped ${off} (Sunday/holiday)` : '',
        worked ? `skipped ${worked} already worked (punched in or visited)` : '',
        ov ? `overwrote ${ov}` : '',
      ].filter(Boolean).join(', ');
      message.success(
        w ? `Marked leave: ${w} day${w === 1 ? '' : 's'}${notes ? `, ${notes}` : ''}`
          : worked ? `Nothing marked — ${notes}.` : 'No working days in that range — nothing marked (all Sundays/holidays).',
      );
      invalidate();
      form.resetFields();
      onClose();
      onLeaveMarked?.(res, body);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to mark leave'),
  });

  const onFinish = async (vals: any) => {
    if (vals.status !== 'LEAVE') { save.mutate(vals); return; }
    const rng = vals.leaveRange as [Dayjs | null, Dayjs | null] | undefined;
    if (!rng || !rng[0] || !rng[1]) { message.error('Pick a valid leave date range (both start and end).'); return; }
    const [from, to] = rng;
    const fromS = from.format('YYYY-MM-DD');
    const toS = to.format('YYYY-MM-DD');
    // Warn-but-allow when the range overwrites existing non-leave attendance.
    let clashes: any[] = [];
    try {
      const existing = await request<any[]>({ url: '/attendance', params: { userId: vals.userId, from: fromS, to: toS } });
      // A day with punches or visits is skipped by the server, never overwritten — so it is not a clash.
      clashes = (existing ?? []).filter((r) => !r.synthetic && r.status !== 'LEAVE' && !r.checkInAt && !(Number(r.visitCount) > 0));
    } catch { /* pre-check is best-effort; the server still records overwrites */ }
    if (clashes.length) {
      const dates = clashes.slice(0, 6).map((c) => c.logDate).join(', ');
      const ok = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: 'Overwrite existing attendance?',
          content: `${clashes.length} day${clashes.length === 1 ? '' : 's'} in this range already have attendance (${dates}${clashes.length > 6 ? '…' : ''}). Marking leave will overwrite ${clashes.length === 1 ? 'it' : 'them'}.`,
          okText: 'Overwrite & mark leave', okButtonProps: { danger: true }, cancelText: 'Cancel',
          onOk: () => resolve(true), onCancel: () => resolve(false),
        });
      });
      if (!ok) return;
    }
    leaveMut.mutate({ userId: vals.userId, from: fromS, to: toS, note: vals.note });
  };

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Mark attendance (on behalf)"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending || leaveMut.isPending} onClick={() => form.submit()}>{isLeave ? 'Mark leave' : 'Save'}</Button></>}>
      <Form form={form} layout="vertical" onFinish={onFinish}
        initialValues={{ logDate: dayjs(), status: canOnBehalf ? 'PRESENT' : 'LEAVE' }}>
        <Form.Item name="userId" label="Staff" rules={[{ required: true, message: 'Pick a staff member' }]}>
          <Select showSearch optionFilterProp="name" optionLabelProp="name" placeholder="Select staff" options={staffOptions} />
        </Form.Item>
        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select options={statusOptions.map((v) => hueOption(
            v, v.replace('_', ' '),
            STATUS_COLOR[v] === 'neutral' ? undefined : TAG_TONE_HUE[STATUS_COLOR[v] ?? 'neutral'],
          ))} />
        </Form.Item>
        {isLeave ? (
          <Form.Item name="leaveRange" label="Leave period" rules={[{ required: true, message: 'Pick the leave date range' }]}
            extra="Sundays and holidays in the range are skipped automatically. Future dates allowed.">
            <DatePicker.RangePicker className="w-full" format="DD MMM YYYY" />
          </Form.Item>
        ) : (
          <>
            <Form.Item name="logDate" label="Date" rules={[{ required: true }]}>
              <DatePicker className="w-full" disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
            </Form.Item>
            <Row gutter={12}>
              {/* A date-and-time field needs ~230px to show its value; two side by side do not fit a phone, so they stack there. */}
              <Col xs={24} sm={12}><Form.Item name="checkInAt" label="Check-in (optional)"><DatePicker showTime={{ minuteStep: 5 }} format="DD MMM YYYY hh:mm A" className="w-full" /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="checkOutAt" label="Check-out (optional)"><DatePicker showTime={{ minuteStep: 5 }} format="DD MMM YYYY hh:mm A" className="w-full" /></Form.Item></Col>
            </Row>
          </>
        )}
        <Form.Item name="note" label={isLeave ? 'Reason / note' : 'Reason for marking on behalf'}
          rules={isLeave ? [] : [{ required: true, message: 'A reason is required (audited)' }, { min: 5, message: 'Give a bit more detail' }]}>
          <Input.TextArea rows={2} placeholder={isLeave ? 'e.g. Approved leave…' : 'Why are you marking this for the rep? e.g. no network at site, rep forgot to punch…'} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
